import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Service for managing user-related API calls
 */
export const userService = {
  /**
   * Get all users (admin/manager only)
   * @param {Object} options Pagination and search options
   * @param {number} options.page Page number (default: 1)
   * @param {number} options.limit Users per page (default: 10)
   * @param {string} options.search Search term (optional)
   * @returns {Promise<Object>} Paginated users data with pagination info
   */
  getAllUsers: async (options = {}) => {
    const { page = 1, limit = 10, search = '' } = options;
    const queryParams = new URLSearchParams();
    
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    
    const queryString = queryParams.toString();
    const url = `${API_URL}/api/users${queryString ? `?${queryString}` : ''}`;
    
    const response = await axios.get(url);
    return response.data;
  },

  /**
   * Get user by ID
   * @param {string|number} id User ID
   * @returns {Promise<Object>} User data
   */
  getUserById: async (id) => {
    const response = await axios.get(`${API_URL}/api/users/${id}`);
    return response.data;
  },

  /**
   * Create a new user (admin/manager only)
   * @param {Object} userData User data
   * @returns {Promise<Object>} Created user
   */
  createUser: async (userData) => {
    const response = await axios.post(`${API_URL}/api/users`, userData);
    return response.data;
  },

  /**
   * Update a user (admin/manager only)
   * @param {string|number} id User ID
   * @param {Object} userData User data to update
   * @returns {Promise<Object>} Updated user
   */
  updateUser: async (id, userData) => {
    const response = await axios.put(`${API_URL}/api/users/${id}`, userData);
    return response.data;
  },

  /**
   * Delete a user (admin/manager only)
   * @param {string|number} id User ID
   * @returns {Promise<void>}
   */
  deleteUser: async (id) => {
    try {
      // Get token for auth
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Set headers for this request
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Make the direct delete request
      const response = await axios.delete(`${API_URL}/api/users/${id}`, { headers });
      
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  /**
   * Get only legal professionals with pagination and search
   * @param {Object} options Pagination and search options
   * @param {number} options.page Page number (default: 1)
   * @param {number} options.limit Users per page (default: 10)
   * @param {string} options.search Search term (optional)
   * @returns {Promise<Object>} Paginated legal professionals data with pagination info
   */
  getLegalProfessionals: async (options = {}, retryCount = 0) => {
    const { page = 1, limit = 10, search = '' } = options;
    const queryParams = new URLSearchParams();
    
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    
    const queryString = queryParams.toString();
    const url = `${API_URL}/api/users/legal-professionals${queryString ? `?${queryString}` : ''}`;
    
    try {
      console.log(`Fetching legal professionals from ${url}`);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Legal professionals data received:', {
        count: response.data?.users?.length || 0,
        total: response.data?.pagination?.total || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching legal professionals:', error);
      
      // Retry mechanism
      if (retryCount < 2) {
        console.log(`Retrying getLegalProfessionals (${retryCount + 1}/2)...`);
        // Exponential backoff
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return userService.getLegalProfessionals(options, retryCount + 1);
      }
      
      // If we've reached max retries, throw a user-friendly error
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Failed to load legal professionals. Please try again later.'
      );
    }
  },

  /**
   * Get only managers with pagination and search
   * @param {Object} options Pagination and search options
   * @param {number} options.page Page number (default: 1)
   * @param {number} options.limit Users per page (default: 10)
   * @param {string} options.search Search term (optional)
   * @returns {Promise<Object>} Paginated managers data with pagination info
   */
  getManagers: async (options = {}, retryCount = 0) => {
    const { page = 1, limit = 10, search = '' } = options;
    const queryParams = new URLSearchParams();
    
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (search) queryParams.append('search', search);
    
    const queryString = queryParams.toString();
    const url = `${API_URL}/api/users/managers${queryString ? `?${queryString}` : ''}`;
    
    try {
      console.log(`Fetching managers from ${url}`);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Managers data received:', {
        count: response.data?.users?.length || 0,
        total: response.data?.pagination?.total || 0
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching managers:', error);
      
      // Retry mechanism
      if (retryCount < 2) {
        console.log(`Retrying getManagers (${retryCount + 1}/2)...`);
        // Exponential backoff
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return userService.getManagers(options, retryCount + 1);
      }
      
      // If we've reached max retries, throw a user-friendly error
      throw new Error(
        error.response?.data?.error || 
        error.message || 
        'Failed to load managers. Please try again later.'
      );
    }
  },

  /**
   * Get a user's claimed profile information
   * @param {string|number} id User ID
   * @returns {Promise<Object>} Claimed profile data
   */
  getClaimedProfile: async (id) => {
    const response = await axios.get(`${API_URL}/api/users/${id}/claimed-profile`);
    return response.data;
  },
  
  /**
   * Set a user's claimed profile status
   * @param {string|number} id User ID
   * @param {Object} payload Claim payload
   * @param {boolean} payload.is_claimed Whether the profile is claimed
   * @param {number|null} payload.organisation_id The organization ID to associate (required if is_claimed=true)
   * @returns {Promise<Object>} Response data
   */
  setClaimedProfile: async (id, payload) => {
    const response = await axios.post(`${API_URL}/api/users/${id}/set-claimed-profile`, payload);
    return response.data;
  },
  
  /**
   * Get a legal professional's user profile
   * @param {string|number} id User ID
   * @returns {Promise<Object>} User profile data
   */
  getUserProfile: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${id}/profile`);
      return response.data;
    } catch (error) {
      // If the specific endpoint isn't available, fall back to basic user data
      console.log('Falling back to basic user data');
      const userData = await axios.get(`${API_URL}/api/users/${id}`);
      return userData.data;
    }
  },
  
  /**
   * Get organizations/solicitors with optional search
   * @param {string} searchTerm Search term for filtering organizations
   * @param {number} limit Number of results to return (default: 10)
   * @returns {Promise<Object[]>} Array of organizations
   */
  getOrganizations: async (searchTerm = '', limit = 10) => {
    const response = await axios.get(`${API_URL}/api/solicitors?search=${searchTerm}&limit=${limit}`);
    return response.data;
  },

  /**
   * Get the current user's profile
   * @returns {Promise<Object>} Current user's profile data
   */
  getCurrentUserProfile: async () => {
    try {
      console.log('Fetching current user profile from API');
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${API_URL}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error('Failed to retrieve user profile data');
    }
  },
  
  /**
   * Update the current user's profile
   * @param {Object} profileData - The profile data to update
   * @returns {Promise<Object>} Updated profile data
   */
  updateProfile: async (profileData) => {
    try {
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Make the API call with authorization header
      const response = await axios.put(`${API_URL}/api/users/profile`, profileData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  },

  /**
   * Upload verification documents and submit claim
   * 
   * @param {Number} userId User ID
   * @param {Number} organisationId Organization ID to claim
   * @param {Object} claimData Claim data including documents
   * @returns {Promise<Object>} Response data
   */
  uploadVerificationDocuments: async (userId, organisationId, claimData) => {
    // Create form data for file upload
    const formData = new FormData();
    
    // Add document files
    if (claimData.documentFiles && claimData.documentFiles.length > 0) {
      claimData.documentFiles.forEach((file) => {
        formData.append('documents', file);
      });
    }
    
    // Add other data
    formData.append('documentType', claimData.documentType);
    formData.append('additionalInfo', claimData.additionalInfo || '');
    formData.append('organisationId', organisationId);
    
    try {
      console.log('Sending verification documents:', { 
        userId, 
        organisationId, 
        documentType: claimData.documentType,
        filesCount: claimData.documentFiles?.length || 0 
      });
      
      const response = await axios.post(
        `${API_URL}/api/users/${userId}/verification-documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error uploading documents:', error);
      // Provide more detailed error info
      const errorMessage = error.response?.data?.error || 'Failed to upload documents';
      const errorDetails = error.response?.data?.details || '';
      
      if (errorMessage === 'User is not a legal professional') {
        throw new Error('Your account is not set as a legal professional. Please contact support to update your account type.');
      }
      
      throw new Error(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`);
    }
  },

  /**
   * Get claim status with verification details
   * 
   * @param {Number} userId User ID
   * @returns {Promise<Object>} Claim status data
   */
  getClaimStatus: async (userId) => {
    const response = await axios.get(`${API_URL}/api/users/${userId}/claim-status`);
    return response.data;
  },

  /**
   * Get organization claim status
   * 
   * @param {Number} organisationId Organization ID
   * @returns {Promise<Object>} Organization claim status data
   */
  getOrganizationClaimStatus: async (organisationId) => {
    const response = await axios.get(`${API_URL}/api/users/claims/organization/${organisationId}`);
    return response.data;
  },

  /**
   * Get all pending claims
   * 
   * @returns {Promise<Array>} Array of pending claims
   */
  getPendingClaims: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(`${API_URL}/api/users/claims/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Received pending claims:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching pending claims:', error);
      throw error;
    }
  },

  /**
   * Approve a profile claim
   * 
   * @param {Number} userId User ID
   * @param {Number} organisationId Organization ID
   * @returns {Promise<Object>} Response data
   */
  approveProfileClaim: async (userId, organisationId) => {
    const response = await axios.post(
      `${API_URL}/api/users/claims/${userId}/${organisationId}/approve`
    );
    return response.data;
  },

  /**
   * Reject a profile claim
   * 
   * @param {Number} userId User ID
   * @param {Number} organisationId Organization ID
   * @param {Object} data Rejection data
   * @param {String} data.reason Rejection reason
   * @returns {Promise<Object>} Response data
   */
  rejectProfileClaim: async (userId, organisationId, data = {}) => {
    const response = await axios.post(
      `${API_URL}/api/users/claims/${userId}/${organisationId}/reject`,
      data
    );
    return response.data;
  },

  /**
   * Remove verification
   * 
   * @param {Number} organisationId Organization ID
   * @param {String} reason Rejection reason
   * @returns {Promise<void>}
   */
  removeVerification: async (organisationId, reason) => {
    const response = await axios.post(
      `${API_URL}/api/organisations/${organisationId}/remove-verification`,
      { reason }
    );
    return response.data;
  },

  /**
   * Get verification document with authentication
   * 
   * @param {Number} userId User ID
   * @param {String} filename Document filename
   * @returns {Promise<Blob>} Document blob data
   */
  getVerificationDocument: async (userId, filename) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await axios.get(
        `${API_URL}/api/users/documents/${userId}/${filename}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob' // Important for binary data
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching document:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load document';
      throw new Error(errorMessage);
    }
  },

  // Alias maintained for backward compatibility
  getProfile: async () => {
    return userService.getCurrentUserProfile();
  },

  /**
   * Get legal professional dashboard data
   * @returns {Promise<Object>} Dashboard data including profile, appointments, enquiries, and statistics
   */
  getLegalProfessionalDashboard: async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${API_URL}/api/users/legal-professional/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching legal professional dashboard:', error);
      throw new Error(error.response?.data?.error || 'Failed to load dashboard data');
    }
  },
};

export default userService; 